import { useTranslation } from '@cobliteam/i18n';
import { lazy } from '@cobliteam/lazy-with-retries';
import { Button, Tooltip } from '@cobliteam/ui/components';
import { useDebounce } from '@cobliteam/ui/hooks';
import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import { Suspense, useEffect, useRef, useState } from 'react';
import AlertFooter from '../components/AlertFooter';
import { CommandDialog, CommandInput } from '../components/Command';
import GlobalSearchTrigger from '../components/GlobalSearchTrigger';
import GlobalSearchSidebar from '../containers/GlobalSearchSidebar';
import { VirtualizedList } from '../containers/VirtualizedList';
import { trackGlobalSearchEvents } from '../helpers';
import { useKeyboardNavigation } from '../hooks/useKeyboardNavigation';
import useSearch from '../hooks/useSearch';
import { EnumCategory } from '../types';

// Detail panes render only after a user opens the palette AND selects a row.
// Keep them out of the entry chunk — GeofenceDetails in particular drags leaflet
// (~186 KB with markercluster) into every page load through the always-mounted
// GlobalSearch component in AppLayout.
const DeviceDetails = lazy(() => import('../components/Details/Device/DeviceDetails'));
const DriverDetails = lazy(() => import('../components/Details/Driver/DriverDetails'));
const GeofenceDetails = lazy(() => import('../components/Details/Geofence/GeofenceDetails'));
const PageDetail = lazy(() => import('../components/Details/Page/PageDetail'));
const VehicleDetails = lazy(() => import('../components/Details/Vehicle/VehicleDetails'));
const VehicleGroupDetails = lazy(() => import('../components/Details/VehicleGroup/VehicleGroupDetails'));

interface GlobalSearchContentProps {
  handleUpdateData: () => void;
  isRefetching: boolean;
}

const GlobalSearchContent = ({ handleUpdateData, isRefetching }: GlobalSearchContentProps) => {
  const { t } = useTranslation('global-search');
  const searchRef = useRef<HTMLInputElement>(null);
  const [inputSearch, setInputSearch] = useState('');
  const debouncedSearch = useDebounce(inputSearch, 400);

  const { isOpen, selectedItem, setSearch, setIsOpen } = useSearch();

  // Add keyboard navigation
  useKeyboardNavigation();

  const handleEscapeKeyDown = (e: Event) => {
    e.preventDefault();
    setSearch('');
    setInputSearch('');
    setIsOpen(false);
  };

  const onHandleUpdateData = () => {
    handleUpdateData();
    trackGlobalSearchEvents.updateData();
  };

  useEffect(() => {
    if (isOpen) {
      setSearch(debouncedSearch);
      if (debouncedSearch) trackGlobalSearchEvents.search(debouncedSearch);
    }

    return () => {
      if (!isOpen) {
        setSearch('');
        setInputSearch('');
      }
    };
  }, [debouncedSearch, setSearch, isOpen]);

  return (
    <>
      <GlobalSearchTrigger
        autoFocus={isOpen}
        onOpen={() => {
          trackGlobalSearchEvents.openSearch();
          setIsOpen(true);
        }}
      />
      <CommandDialog size='lg' open={isOpen} onOpenChange={setIsOpen} shouldFilter={false} onEscapeKeyDown={handleEscapeKeyDown}>
        <div className='flex items-center justify-between gap-2 border-gray-4 border-b pr-2'>
          <CommandInput ref={searchRef} autoFocus disabled={isRefetching} placeholder={t('search.placeholder')} value={inputSearch} onValueChange={setInputSearch} />
          <Tooltip content={t('search.update_fleet_data')}>
            <Button variant='ghost' size='sm' onClick={onHandleUpdateData} className='p-2'>
              <Icon name='refresh-cw' className={cn(isRefetching && 'animate-spin')} />
            </Button>
          </Tooltip>
        </div>
        <div className='grid h-[500px] grid-cols-12'>
          <section className='col-span-3 border-gray-4 border-r'>
            <GlobalSearchSidebar />
          </section>
          <section className='col-span-4 overflow-auto border-gray-4 border-r'>
            <VirtualizedList />
          </section>
          <section className='col-span-5 flex gap-2'>
            <Suspense fallback={null}>
              {selectedItem?.category.id === EnumCategory.VEHICLES && <VehicleDetails />}
              {selectedItem?.category.id === EnumCategory.DRIVERS && <DriverDetails />}
              {selectedItem?.category.id === EnumCategory.DEVICES && <DeviceDetails />}
              {selectedItem?.category.id === EnumCategory.VEHICLE_GROUPS && <VehicleGroupDetails />}
              {selectedItem?.category.id === EnumCategory.GEOFENCES && <GeofenceDetails />}
              {selectedItem?.category.id === EnumCategory.PAGES && <PageDetail />}
            </Suspense>
          </section>
        </div>
        <AlertFooter />
      </CommandDialog>
    </>
  );
};

export default GlobalSearchContent;
